import React from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage } from 'react-intl';

import { currencyCode } from 'Helpers/Geo/Country';
import { formatDrmName } from 'Helpers/Product/FormatMetaNames';
import SpecialFormattedNumber from 'Components/Price/SpecialFormattedNumber';

const DOWNLOAD_BUNDLE_TYPES = ['software-bundle', 'elearning-bundle', 'audio-bundle'];
/**
 * The quick look's key points (see HitCardQuickLook), shown as tags in the hit card popup for
 * bundles (606PopupHitCardBundleCoverGrid). The messages are the quick look's own, ids and all,
 * so they share its translations.
 */
const HitCardPopupBundleTags = ({ hit, drm }) => {
  const {
    type,
    display_type: displayType,
    game_total: gameTotal = 0,
    dlc_total: dlcTotal = 0,
    fullPrice,
  } = hit;

  const isPickAndMix = type === 'pick-and-mix';
  const priceValue = fullPrice?.[currencyCode];
  const price = <SpecialFormattedNumber value={priceValue} style="currency" />;

  const choose = isPickAndMix &&
    <FormattedMessage id="quicklook.total-games.choose" defaultMessage="Choose from " />;

  const from = <FormattedMessage id="quicklook.pnm-from" defaultMessage="From {price}" values={{ price }} />;

  // A bundle of nothing but DLC says "4 DLC" rather than "0 Games + 4 DLC"
  const dlcOnly = !gameTotal && dlcTotal >= 1 &&
    <FormattedMessage id="quicklook.total-dlc-only" defaultMessage="{dlcTotal} DLC" values={{ dlcTotal }} />;

  const redeem = drm &&
    <FormattedMessage id="quicklook.redeem" defaultMessage="Redeem on {validDrm} " values={{ validDrm: formatDrmName(drm) }} />;

  let total;
  let worth;
  let delivery;

  if (displayType === 'comic-bundle') {
    total = <FormattedMessage id="quicklook.total-comics" defaultMessage="{gameTotal, plural, one {# Comic} other {# Comics}}" values={{ gameTotal }} />;
    worth = <FormattedMessage id="quicklook.worth-of-comics" defaultMessage="{price} worth of Comics" values={{ price }} />;
    delivery = <FormattedMessage id="quicklook.mulidownload" defaultMessage="Multi-format DRM-Free downloads" />;
  } else if (displayType === 'book-bundle') {
    total = <FormattedMessage id="quicklook.total-books" defaultMessage="{gameTotal, plural, one {# eBook} other {# eBooks}}" values={{ gameTotal }} />;
    worth = <FormattedMessage id="quicklook.worth-of-ebooks" defaultMessage="{price} worth of ebooks" values={{ price }} />;
    delivery = <FormattedMessage id="quicklook.ebook-delivery" defaultMessage="Multi-format downloads" />;
  } else if (DOWNLOAD_BUNDLE_TYPES.includes(displayType)) {
    total = dlcOnly ||
      <FormattedMessage id="quicklook.total-products" defaultMessage="{gameTotal, plural, one {# Product} other {# Products}}" values={{ gameTotal }} />;
    worth = <FormattedMessage id="quicklook.worth-of-products" defaultMessage="{price} worth of products" values={{ price }} />;
    delivery = redeem;
  } else {
    total = dlcOnly || (
      <React.Fragment>
        <FormattedMessage id="quicklook.total-games" defaultMessage="{gameTotal, plural, one {# Game} other {# Games}}" values={{ gameTotal }} />
        {dlcTotal >= 1 &&
          <React.Fragment>
            {' '}
            <FormattedMessage id="quicklook.total-dlc" defaultMessage="+ {dlcTotal} DLC" values={{ dlcTotal }} />
          </React.Fragment>
        }
      </React.Fragment>
    );
    worth = <FormattedMessage id="quicklook.worth-of-games" defaultMessage="{price} worth of games" values={{ price }} />;
    delivery = redeem;
  }

  const tags = [
    (gameTotal > 0 || dlcTotal > 0) && { key: 'total', content: <React.Fragment>{choose}{total}</React.Fragment> },
    typeof priceValue === 'number' && { key: 'price', content: isPickAndMix ? from : worth },
    !!delivery && { key: 'delivery', content: delivery },
  ].filter(Boolean);

  if (tags.length === 0) return null;

  return (
    <div className="tag-column-container tag-column-container--wrap">
      {tags.map(tag =>
        <div className="tag-column" key={tag.key}>
          <div className="tag">
            {tag.content}
          </div>
        </div>,
      )}
    </div>
  );
};

HitCardPopupBundleTags.propTypes = {
  hit: PropTypes.shape({
    type: PropTypes.string,
    display_type: PropTypes.string,
    game_total: PropTypes.number,
    dlc_total: PropTypes.number,
    fullPrice: PropTypes.shape({}),
  }).isRequired,
  drm: PropTypes.string,
};

HitCardPopupBundleTags.defaultProps = {
  drm: '',
};

export default HitCardPopupBundleTags;
